import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  GraduationCap, CheckCircle, ArrowRight, Loader2, 
  Trophy, Star, Target, BookOpen, Clock, Lock, Unlock, Crown
} from 'lucide-react';
import { getUnlockedLevelsByPlacement, isAdvancedModuleAvailable, getAllLevels as getStaticLevels } from '../data/kariyerCurriculum';
import { fetchAllLevels } from '../services/curriculumService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlacementTest = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [stage, setStage] = useState('intro'); // intro, test, result
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [levels, setLevels] = useState([]);

  // Load levels for result display
  useEffect(() => {
    loadLevels();
  }, []);

  const loadLevels = async () => {
    try {
      const apiLevels = await fetchAllLevels('english');
      if (apiLevels && apiLevels.length > 0) {
        setLevels(apiLevels);
        return;
      }
    } catch (error) {
      console.log('API error, falling back to static');
    }
    setLevels(getStaticLevels());
  };

  // Timer for test
  useEffect(() => {
    if (stage === 'test' && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && stage === 'test') {
      handleFinishTest();
    }
  }, [timeLeft, stage]);

  const startTest = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/exams/placement-test/start`, {
        language: 'en'
      }, { withCredentials: true });
      
      setQuestions(res.data.questions || []);
      setTimeLeft(30 * 60); // 30 dakika
      setStage('test');
    } catch (error) {
      console.error('Test başlatılamadı:', error);
      toast.error('Test başlatılamadı. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (answerIndex) => {
    setSelectedAnswer(answerIndex);
  };

  const handleNext = () => {
    if (selectedAnswer === null) {
      toast.error('Lütfen bir cevap seçin');
      return;
    }

    const newAnswers = [...answers, {
      questionId: currentQuestion,
      answer: selectedAnswer,
      correct: selectedAnswer === questions[currentQuestion].correct_answer
    }];
    setAnswers(newAnswers);
    setSelectedAnswer(null);

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleFinishTest(newAnswers);
    }
  };

  const handleFinishTest = async (finalAnswers = answers) => {
    setLoading(true);
    try {
      const correctCount = finalAnswers.filter(a => a.correct).length;
      const score = Math.round((correctCount / questions.length) * 100);
      
      // Determine level based on score (30 questions: A1-6, A2-6, B1-6, B2-6, C1-6)
      let level = 'A1';
      if (score >= 85) level = 'C1';
      else if (score >= 70) level = 'B2';
      else if (score >= 55) level = 'B1';
      else if (score >= 40) level = 'A2';
      else level = 'A1';

      // Save result to backend
      const res = await axios.post(`${API}/exams/placement-test/complete`, {
        answers: finalAnswers,
        score: score,
        level: level,
        total_questions: questions.length,
        correct_answers: correctCount
      }, { withCredentials: true });

      setResult({
        score,
        level,
        correctCount,
        totalQuestions: questions.length
      });

      // Update user context
      setUser(prev => ({
        ...prev,
        placement_test_completed: true,
        current_level: level
      }));

      setStage('result');
      toast.success('Tebrikler! Seviye tespiti tamamlandı.');
    } catch (error) {
      console.error('Test sonucu kaydedilemedi:', error);
      toast.error('Sonuç kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  // Intro Stage
  if (stage === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full shadow-2xl border-0">
          <CardHeader className="text-center bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-t-lg py-8">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <GraduationCap className="w-10 h-10" />
            </div>
            <CardTitle className="text-3xl">Seviye Tespit Sınavı</CardTitle>
            <p className="text-white/80 mt-2">Hoş geldin, {user?.name?.split(' ')[0] || 'Öğrenci'}! 👋</p>
          </CardHeader>
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Eğitime başlamadan önce seviyeni belirleyelim
              </h2>
              <p className="text-gray-600">
                Bu test senin İngilizce seviyeni tespit edecek ve sana uygun içerikleri sunmamızı sağlayacak.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-blue-50 rounded-xl text-center">
                <BookOpen className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-800">30 Soru</p>
                <p className="text-sm text-gray-500">Çoktan seçmeli</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl text-center">
                <Clock className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-800">30 Dakika</p>
                <p className="text-sm text-gray-500">Maksimum süre</p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <Target className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-800">A1 - C1</p>
                <p className="text-sm text-gray-500">Seviye belirleme</p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8">
              <p className="text-yellow-800 text-sm">
                ⚠️ <strong>Önemli:</strong> Test başladıktan sonra çıkamazsınız. 
                Lütfen sessiz bir ortamda ve dikkatiniz dağılmadan çözün.
              </p>
            </div>

            <Button 
              onClick={startTest} 
              disabled={loading}
              className="w-full py-6 text-lg bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Hazırlanıyor...
                </>
              ) : (
                <>
                  Teste Başla
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Test Stage
  if (stage === 'test' && questions.length > 0) {
    const question = questions[currentQuestion];
    const progress = ((currentQuestion + 1) / questions.length) * 100;

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-lg px-4 py-2">
                {currentQuestion + 1} / {questions.length}
              </Badge>
            </div>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
              timeLeft < 300 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
            }`}>
              <Clock className="w-5 h-5" />
              <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
            </div>
          </div>

          {/* Progress */}
          <Progress value={progress} className="h-2 mb-8" />

          {/* Question Card */}
          <Card className="shadow-xl border-0 mb-6">
            <CardContent className="p-8">
              <p className="text-xl font-semibold text-gray-800 mb-8">
                {question.question}
              </p>

              <div className="space-y-3">
                {question.options?.map((option, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(idx)}
                    className={`w-full p-4 text-left rounded-xl border-2 transition-all ${
                      selectedAnswer === idx
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        selectedAnswer === idx
                          ? 'bg-indigo-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className="font-medium">{option}</span>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Next Button */}
          <Button
            onClick={handleNext}
            disabled={selectedAnswer === null || loading}
            className="w-full py-6 text-lg bg-gradient-to-r from-indigo-500 to-purple-600"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : currentQuestion === questions.length - 1 ? (
              <>
                Testi Bitir
                <CheckCircle className="w-5 h-5 ml-2" />
              </>
            ) : (
              <>
                Sonraki Soru
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  // Result Stage
  if (stage === 'result' && result) {
    const levelColors = {
      'A0': 'from-lime-400 to-green-500',
      'A1': 'from-green-400 to-green-600',
      'A2': 'from-blue-400 to-blue-600',
      'B1': 'from-yellow-400 to-orange-500',
      'B2': 'from-purple-400 to-purple-600',
      'C1': 'from-red-400 to-red-600',
      'C2': 'from-gray-700 to-gray-900'
    };

    const levelDescriptions = {
      'A0': 'Sıfır - Baştan başlıyoruz',
      'A1': 'Başlangıç - Temel kelime ve cümleler',
      'A2': 'Temel - Günlük konuşmalar',
      'B1': 'Orta - Bağımsız iletişim',
      'B2': 'Orta-Üst - Akıcı iletişim',
      'C1': 'İleri - Profesyonel seviye',
      'C2': 'Uzman - Ana dil seviyesi'
    };

    // Açılan seviyeleri hesapla
    const maxUnlocked = getUnlockedLevelsByPlacement(result.level);
    const hasAdvancedAccess = isAdvancedModuleAvailable(result.level);
    const allLevels = levels.length > 0 ? levels : getStaticLevels();

    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 py-8">
        <div className="max-w-xl mx-auto">
          <Card className="shadow-2xl border-0 overflow-hidden mb-6">
            <div className={`bg-gradient-to-r ${levelColors[result.level] || levelColors['A1']} text-white py-12 text-center`}>
              <Trophy className="w-16 h-16 mx-auto mb-4" />
              <h1 className="text-4xl font-bold mb-2">Tebrikler! 🎉</h1>
              <p className="text-white/80">Seviye tespiti tamamlandı</p>
            </div>
            
            <CardContent className="p-8">
              {/* Level Badge */}
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-r ${levelColors[result.level] || levelColors['A1']} text-white text-4xl font-bold mb-4`}>
                  {result.level}
                </div>
                <h2 className="text-2xl font-bold text-gray-800">{result.level} Seviyesi</h2>
                <p className="text-gray-600">{levelDescriptions[result.level]}</p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="p-4 bg-green-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-green-600">{result.correctCount}</p>
                  <p className="text-sm text-gray-600">Doğru Cevap</p>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl text-center">
                  <p className="text-3xl font-bold text-blue-600">%{result.score}</p>
                  <p className="text-sm text-gray-600">Başarı Oranı</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Açılan Seviyeler */}
          <Card className="shadow-xl border-0 mb-6">
            <CardContent className="p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <Unlock className="w-5 h-5 text-green-500" />
                Senin İçin Açılan Seviyeler
              </h3>
              
              <div className="space-y-2">
                {allLevels.map((level, index) => {
                  const isUnlocked = index <= maxUnlocked || (level.isAdvanced && hasAdvancedAccess);
                  const isAdvanced = level.isAdvanced || false;
                  
                  return (
                    <div 
                      key={level.id}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
                        isUnlocked 
                          ? isAdvanced 
                            ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200' 
                            : 'bg-green-50 border border-green-200'
                          : 'bg-gray-50 border border-gray-200 opacity-50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${
                        isUnlocked
                          ? isAdvanced
                            ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white'
                            : 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-400'
                      }`}>
                        {isUnlocked ? (
                          isAdvanced ? <Crown className="w-5 h-5" /> : level.icon
                        ) : (
                          <Lock className="w-4 h-4" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className={`font-medium ${isUnlocked ? 'text-gray-800' : 'text-gray-400'}`}>
                          Seviye {level.level}: {level.title}
                        </p>
                        <p className={`text-xs ${isUnlocked ? 'text-gray-500' : 'text-gray-400'}`}>
                          {level.cefrLevel} • {level.units.length} ünite
                        </p>
                      </div>
                      {isUnlocked && (
                        <CheckCircle className={`w-5 h-5 ${isAdvanced ? 'text-amber-500' : 'text-green-500'}`} />
                      )}
                    </div>
                  );
                })}
              </div>

              {hasAdvancedAccess && (
                <div className="mt-4 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-amber-500" />
                    <span className="font-bold text-amber-700">🎉 Özel İleri Modül Açıldı!</span>
                  </div>
                  <p className="text-sm text-amber-600">
                    C1-C2 seviyesinde olduğun için Reading, Writing, Listening, Speaking ve 
                    Havayolu Mülakatları gibi ileri içeriklere erişebilirsin!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info */}
          <div className="bg-indigo-50 rounded-xl p-4 mb-6">
            <p className="text-indigo-800 text-sm">
              🎯 {maxUnlocked + 1} seviye senin için açıldı! 
              {result.level === 'A0' || result.level === 'A1' 
                ? ' Temellerden başlayarak güçlü bir İngilizce altyapısı oluşturacaksın.'
                : ` ${result.level} seviyesine uygun içeriklerle hızlıca ilerlemeye başlayabilirsin.`}
            </p>
          </div>

          <Button 
            onClick={goToDashboard}
            className="w-full py-6 text-lg bg-gradient-to-r from-indigo-500 to-purple-600"
          >
            Eğitime Başla
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // Loading fallback
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
    </div>
  );
};

export default PlacementTest;
